/**
 * [DSS] Print — Hidden Menu + Barcode + Image Toggle + Variant Sync
 *
 * 1. Hidden Menu: 3x Klick auf SKU → Floating Menu mit Druck-Optionen
 * 2. JsBarcode: EAN als Strichcode rendern (gerade Linien)
 * 3. Image Toggle: Produktbild ein-/ausblenden vor Druck
 * 4. Variant Sync: Aktualisiert Print-Sheet bei Variantenwechsel
 */
import { $, $$ } from '../utils/helpers.js';

const SKU_CLICK_THRESHOLD = 3;
const SKU_CLICK_TIMEOUT = 3000;
const JSBARCODE_CDN = 'https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js';

// =====================================================
// 1) HIDDEN MENU — 3x SKU Click
// =====================================================

function initHiddenMenu() {
  const skuEl = $('.product__sku');
  if (!skuEl) {
    return;
  }


  let clickCount = 0;
  let lastClickTime = 0;

  const menu = document.createElement('div');
  menu.className = 'dss-print-hidden-menu';
  menu.setAttribute('role', 'menu');
  menu.setAttribute('aria-label', 'Druck-Optionen');
  menu.innerHTML = `
    <div class="dss-print-hidden-menu__title">Datenblatt</div>
    <button class="dss-print-hidden-menu__btn" data-dss-print-action="with-image" role="menuitem">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>
      Datenblatt mit Bild drucken
    </button>
    <button class="dss-print-hidden-menu__btn" data-dss-print-action="without-image" role="menuitem">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="9" x2="15" y2="15"/><line x1="15" y1="9" x2="9" y2="15"/></svg>
      Datenblatt ohne Bild drucken
    </button>
  `;
  document.body.appendChild(menu);

  function handleSkuClick() {
    const now = Date.now();
    if (now - lastClickTime > SKU_CLICK_TIMEOUT) clickCount = 0;
    lastClickTime = now;
    clickCount++;
    if (clickCount >= SKU_CLICK_THRESHOLD) {
      clickCount = 0;
      toggleMenu(menu);
    }
  }

  skuEl.style.cursor = 'default';
  skuEl.addEventListener('click', (e) => { e.stopPropagation(); handleSkuClick(); });
  skuEl.querySelectorAll('span').forEach((span) => {
    span.addEventListener('click', (e) => { e.stopPropagation(); handleSkuClick(); });
  });

  menu.querySelectorAll('[data-dss-print-action]').forEach((btn) => {
    btn.addEventListener('click', () => {
      hideMenu(menu);
      setImageVisible(btn.getAttribute('data-dss-print-action') === 'with-image');
      setTimeout(() => window.print(), 100);
    });
  });

  document.addEventListener('click', (e) => {
    if (menu.classList.contains('is-visible') && !menu.contains(e.target) && !skuEl.contains(e.target)) {
      hideMenu(menu);
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && menu.classList.contains('is-visible')) hideMenu(menu);
  });
}

function toggleMenu(menu) {
  menu.classList.contains('is-visible') ? hideMenu(menu) : showMenu(menu);
}

function showMenu(menu) {
  menu.classList.add('is-visible');
}

function hideMenu(menu) {
  menu.classList.remove('is-visible');
}

// =====================================================
// 2) IMAGE TOGGLE
// =====================================================

function setImageVisible(visible) {
  const printSection = $('.dss-product-print');
  if (!printSection) return;

  const body = printSection.querySelector('.dss-product-print__body');

  if (visible) {
    printSection.classList.remove('dss-product-print__image-hidden');
    printSection.setAttribute('data-dss-print-show-image', 'true');
    if (body) body.classList.add('dss-product-print__body--with-image');
  } else {
    printSection.classList.add('dss-product-print__image-hidden');
    printSection.setAttribute('data-dss-print-show-image', 'false');
    if (body) body.classList.remove('dss-product-print__body--with-image');
  }
}

// =====================================================
// 3) JsBarcode — EAN rendern
// =====================================================

function loadJsBarcode() {
  return new Promise((resolve) => {
    if (window.JsBarcode) { resolve(); return; }
    const script = document.createElement('script');
    script.src = JSBARCODE_CDN;
    script.onload = resolve;
      script.onerror = () => { resolve(); };
    document.head.appendChild(script);
  });
}

async function renderBarcodes() {
  const barcodeEls = $$('[data-dss-print-barcode]');
  if (!barcodeEls.length) return;

  await loadJsBarcode();

  barcodeEls.forEach((el) => {
    const ean = el.getAttribute('data-dss-print-barcode');
    const svgEl = el.querySelector('svg[data-dss-print-barcode-svg]');
    if (!ean || !svgEl || !window.JsBarcode) return;

    try {
      window.JsBarcode(svgEl, ean, {
        format: 'EAN13',
        width: 1.5,
        height: 50,
        displayValue: false,
        margin: 0,
        background: 'transparent',
        lineColor: '#000000',
        flat: true,
      });
    } catch (err) {
      svgEl.style.display = 'none';
    }
  });
}

// =====================================================
// 4) VARIANT SYNC — Update Print on Variant Change
// =====================================================

function formatPrice(cents) {
  if (!cents && cents !== 0) return '';
  const amount = cents / 100;
  const formatted = new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
  return `${formatted} €`;
}

function updatePrintSection(variant) {
  const printSection = $('.dss-product-print');
  if (!printSection || !variant) return;

  // 4a) Variant title
  const titleEl = printSection.querySelector('[data-dss-print-variant-title]');
  if (titleEl) {
    titleEl.textContent = variant.title && variant.title !== 'Default Title'
      ? `Rahmenhöhe: ${variant.title}`
      : '';
    titleEl.style.display = variant.title && variant.title !== 'Default Title' ? '' : 'none';
  }

  // 4b) Price
  const priceEl = printSection.querySelector('[data-dss-print-price]');
  if (priceEl) {
    if (variant.price) {
      priceEl.textContent = formatPrice(variant.price);
      priceEl.parentElement.style.display = '';
    } else {
      priceEl.parentElement.style.display = 'none';
    }
  }

  // 4c) Compare price
  const compareEl = printSection.querySelector('[data-dss-print-compare-price]');
  if (compareEl) {
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      compareEl.innerHTML = `${formatPrice(variant.compare_at_price)}<sup>²</sup>`;
      compareEl.style.display = '';
    } else {
      compareEl.style.display = 'none';
    }
  }

  // 4d) Barcode + SKU
  const barcodeEl = printSection.querySelector('.dss-product-print__barcode');
  if (barcodeEl) {
    const skuEl = printSection.querySelector('[data-dss-print-sku]');
    if (variant.barcode) {
      barcodeEl.setAttribute('data-dss-print-barcode', variant.barcode);
      barcodeEl.style.display = '';
      if (skuEl) {
        skuEl.textContent = variant.sku || '';
        skuEl.style.display = variant.sku ? '' : 'none';
      }
    } else {
      barcodeEl.style.display = 'none';
    }
  }

  // 4e) Featured image (variant-specific)
  const imgEl = printSection.querySelector('[data-dss-print-img]');
  if (imgEl && variant.featured_image) {
    imgEl.src = variant.featured_image.src;
    imgEl.alt = variant.featured_image.alt || imgEl.alt;
    imgEl.width = variant.featured_image.width;
    imgEl.height = variant.featured_image.height;
  }

  // 4f) Re-render barcodes after variant change
  renderBarcodes();
}

function initVariantChangeSync() {
  // ponytail: subscribe/PUB_SUB_EVENTS are globals from Dawn's pubsub.js + constants.js
  if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

  subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
    const variant = event?.data?.variant;
    if (variant) {
      updatePrintSection(variant);
    }
  });
}

// =====================================================
// INIT
// =====================================================

export function initPrintDatasheet() {
  initHiddenMenu();
  renderBarcodes();
  initVariantChangeSync();

  const printSection = $('.dss-product-print');
  if (printSection) {
    setImageVisible(printSection.getAttribute('data-dss-print-show-image') === 'true');
  }
}
